import { FC } from "react"
import { ConfigAdminFieldLabelHint } from "../../../../../config-admin-form-primitives"
import { LeadworkflowDetails } from "../../../../../../../../types"

interface Props {
  values: LeadworkflowDetails
  setConfigField: (field: string, value: any) => void
}

interface DisclaimerFieldProps {
  label: string
  description: string
  value: string | undefined
  onChange: (val: string) => void
  /** Optional trailing element rendered inline after the label. */
  labelExtra?: React.ReactNode
}

const DisclaimerField: FC<DisclaimerFieldProps> = ({
  label,
  description,
  value,
  onChange,
  labelExtra
}) => (
  <div>
    <div className="flex items-center gap-3">
      <ConfigAdminFieldLabelHint label={label} hint={description} />
      {labelExtra}
    </div>
    <textarea
      value={value ?? ""}
      onChange={e => onChange(e.target.value)}
      rows={6}
      className="w-full px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-md text-gray-700 resize-none focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 transition-colors"
    />
  </div>
)

const LeadworkflowDisclaimers: FC<Props> = ({ values, setConfigField }) => {
  const disclaimers = values.config_json.disclaimers ?? {}
  const showSms = values.config_json.show_txt_msgs_disclaimer === true
  const optional = values.config_json.txt_msgs_disclaimer_optional === true
  const hasTxtMsgs = !!disclaimers.txt_msgs?.trim()

  const setDisclaimer = (key: string, val: string) =>
    setConfigField("disclaimers", { ...disclaimers, [key]: val })

  return (
    <div className="space-y-4 pt-2">
      <DisclaimerField
        label="General"
        description="Shown on the main lead form"
        value={disclaimers.general}
        onChange={val => setDisclaimer("general", val)}
      />
      <DisclaimerField
        label="Lead verification"
        description="Displayed before submitting lead contact info"
        value={disclaimers.lead_verification}
        onChange={val => setDisclaimer("lead_verification", val)}
      />
      <DisclaimerField
        label="Final result"
        description="Shown on the results / confirmation screen"
        value={disclaimers.final_result}
        onChange={val => setDisclaimer("final_result", val)}
      />
      {showSms && (
        <DisclaimerField
          label="SMS consent text"
          description="Borrowers must agree to this before submitting"
          value={disclaimers.txt_msgs}
          onChange={val => setDisclaimer("txt_msgs", val)}
          labelExtra={
            <label className="flex items-center gap-1.5 text-xs text-gray-500 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={optional}
                disabled={!hasTxtMsgs}
                onChange={e =>
                  setConfigField(
                    "txt_msgs_disclaimer_optional",
                    e.target.checked
                  )
                }
                className="h-3.5 w-3.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500 disabled:opacity-40"
              />
              Optional
            </label>
          }
        />
      )}
    </div>
  )
}

export default LeadworkflowDisclaimers
